import { useContext, useState } from 'react'
import type { Country as PhoneCountry } from 'react-phone-number-input'
import { Controller, FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { ColorPicker, FileUploader, PhoneNumberInput, Select } from 'shared/components'
import { InputGroup } from 'shared/molecules'
import { useSelect } from 'shared/hooks'
import { StoreSettingsContext } from 'store'
import type { Country, LanguagesRes } from 'shared/types/general.types'
import { toOptions } from '../../utils/options'

type AccountFieldsProps = {
	form: UseFormReturn<FieldValues>
	accentColor: string
	countries: Country[] | undefined
	isCountriesLoading: boolean
	languages: LanguagesRes | undefined
	isLanguagesLoading: boolean
	onAccentColorChange: (color: string) => void
}

export const AccountFields = ({
	form,
	accentColor,
	countries,
	isCountriesLoading,
	languages,
	isLanguagesLoading,
	onAccentColorChange
}: AccountFieldsProps) => {
	const { t } = useTranslation()
	const settingsCtx = useContext(StoreSettingsContext)
	const [images, setImages] = useState<any[]>([])
	const {
		register,
		control,
		setValue,
		trigger,
		formState: { errors }
	} = form
	const { handleSelectChange } = useSelect({ setValue, trigger })
	const fieldErrors = errors as any

	return (
		<>
			<InputGroup
				id="name"
				label={t('Name')}
				required
				{...register('name')}
				error={fieldErrors.name}
			/>
			<ColorPicker
				id="accent_color"
				label={t('Brand color')}
				color={accentColor}
				onChange={({ hex }) => onAccentColorChange(hex)}
			/>
			<InputGroup id="website" label={t('Website')} {...register('website_url')} />
			<PhoneNumberInput
				id="phone"
				variant="form"
				control={control}
				defaultCountry={settingsCtx?.storeSettings?.address?.country as PhoneCountry}
				label={t('Phone')}
				{...register('phone')}
			/>
			<InputGroup id="email" type="email" label={t('Email')} {...register('email')} />
			<InputGroup
				id="default_email"
				type="email"
				label={t('Default email')}
				{...register('default_from_email')}
			/>
			<Controller
				name="default_language"
				control={control}
				render={({ field }) => (
					<Select
						id="default_language"
						variant="form"
						isClearable={false}
						label={t('Default language')}
						options={toOptions(languages)}
						{...field}
						isLoading={isLanguagesLoading}
						error={fieldErrors.default_language?.value}
						onChange={(data: any) =>
							handleSelectChange({
								id: 'default_language',
								value: data?.value,
								label: data?.label
							})
						}
					/>
				)}
			/>
			<Controller
				name="country"
				control={control}
				render={({ field }) => (
					<Select
						id="country"
						variant="form"
						isClearable={false}
						isSearchable
						label={t('Country')}
						required
						options={toOptions(countries)}
						{...field}
						isLoading={isCountriesLoading}
						error={fieldErrors.country?.value}
						onChange={(data: any) =>
							handleSelectChange({ id: 'country', value: data?.value, label: data?.label })
						}
					/>
				)}
			/>
			<InputGroup
				id="address"
				label={t('Address')}
				required
				{...register('address.address')}
				error={fieldErrors.address?.address}
			/>
			<div className="grid gap-4 sm:grid-cols-2">
				<InputGroup
					id="city"
					label={t('City')}
					required
					{...register('address.city')}
					error={fieldErrors.address?.city}
				/>
				<InputGroup
					id="postal_code"
					label={t('Postal code')}
					required
					{...register('address.postal_code')}
					error={fieldErrors.address?.postal_code}
				/>
			</div>
			<Controller
				name="logo"
				control={control}
				render={({ field }) => (
					<FileUploader
						label={t('Store logo')}
						{...field}
						images={images}
						setImages={setImages}
						onChange={(data) => setValue('logo', data[0]?.file)}
					/>
				)}
			/>
		</>
	)
}
